import {
  HS_DEFAULT_CAD_EFFECTS_SETTINGS,
  HsCadEffectsSettings,
} from '@lib/core-ts/graphic-render-new/types/cad-effects-settings.interface';
import {
  HS_DEFAULT_VISUAL_QUALITY_SETTINGS,
  HsVisualQualitySettings,
  HsVisualQualityShadingMode,
} from '@lib/core-ts/graphic-render-new/types/visual-quality-settings.interface';

export type ViewerVisualQualityProfile =
  | 'baseline'
  | 'technical'
  | 'presentation'
  | 'high-quality'
  | 'enhanced'
  | 'enhanced-crisp'
  | 'enhanced-drawing'
  | 'enhanced-balanced'
  | 'enhanced-contrast'
  | 'enhanced-studio'
  | 'studio-soft'
  | 'studio-gallery'
  | 'studio-product'
  | 'studio-clay';

export const DEFAULT_VIEWER_VISUAL_QUALITY_PROFILE: ViewerVisualQualityProfile = 'technical';

export const VIEWER_VISUAL_QUALITY_PROFILES: readonly {
  readonly value: ViewerVisualQualityProfile;
  readonly label: string;
  readonly description: string;
}[] = [
  {
    value: 'baseline',
    label: 'Baseline',
    description: 'Flat unlit BIM rendering with the existing white background and anti-aliasing disabled.',
  },
  {
    value: 'technical',
    label: 'Technical',
    description: 'Uses softer inspection lighting and adds a subtle Fresnel silhouette cue.',
  },
  {
    value: 'presentation',
    label: 'Presentation',
    description: 'Raises fill and directional light with bounded specular highlights for surface separation.',
  },
  {
    value: 'high-quality',
    label: 'High quality',
    description: 'Uses controlled studio lighting with contrast and exposure adjustment.',
  },
  {
    value: 'enhanced',
    label: 'Enhanced',
    description:
      'Soft lighting plus screen-space ambient occlusion, crease and silhouette edges, tonemapping and a gradient background. Heavier effects run at reduced quality while the camera moves.',
  },
  {
    value: 'enhanced-studio',
    label: 'Enhanced (studio)',
    description:
      'Enhanced variant with a studio look: warm-grey vignette backdrop, strong wide ambient occlusion, heavy dark edges and unmodified colors.',
  },
  {
    value: 'enhanced-crisp',
    label: 'Enhanced: crisp',
    description:
      'Crisp technical look: sharp fine outlines, moderate tight occlusion, light filmic curve and sharpening.',
  },
  {
    value: 'enhanced-drawing',
    label: 'Enhanced: drawing',
    description: 'Drawing look: bold 2 px outlines, flat bright fill and no tonemapping.',
  },
  {
    value: 'enhanced-balanced',
    label: 'Enhanced: balanced',
    description: 'Balanced look: medium outlines, deeper occlusion and a subtle camera key light.',
  },
  {
    value: 'enhanced-contrast',
    label: 'Enhanced: high contrast',
    description: 'High-contrast look: strong wide occlusion, punchy filmic curve and crisp outlines.',
  },
  {
    value: 'studio-soft',
    label: 'Studio: soft',
    description: 'Soft studio look: wide soft occlusion, warm filmic curve, key light and faint outlines.',
  },
  {
    value: 'studio-gallery',
    label: 'Studio: bright gallery',
    description: 'High-key studio look: raised exposure, gentle occlusion and almost no outlines.',
  },
  {
    value: 'studio-product',
    label: 'Studio: product shot',
    description: 'Product-shot look: strong key light and occlusion, full filmic curve and warm white balance.',
  },
  {
    value: 'studio-clay',
    label: 'Studio: clay',
    description: 'Clay look: desaturated model with a soft key light and deep occlusion.',
  },
] as const;

/** Profiles that run the screen-space CAD post passes (occlusion, edges, tonemapping, backdrop). */
export const VIEWER_CAD_EFFECT_PROFILES: ReadonlySet<ViewerVisualQualityProfile> = new Set<ViewerVisualQualityProfile>([
  'enhanced',
  'enhanced-crisp',
  'enhanced-drawing',
  'enhanced-balanced',
  'enhanced-contrast',
  'enhanced-studio',
  'studio-soft',
  'studio-gallery',
  'studio-product',
  'studio-clay',
]);

export type ViewerVisualQualityAntiAliasing = 'off' | 'msaa2' | 'msaa4' | 'msaa8';

export const VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS: readonly {
  readonly value: ViewerVisualQualityAntiAliasing;
  readonly label: string;
}[] = [
  { value: 'off', label: 'Off' },
  { value: 'msaa2', label: 'MSAA 2x' },
  { value: 'msaa4', label: 'MSAA 4x' },
  { value: 'msaa8', label: 'MSAA 8x' },
] as const;

export type ViewerVisualQualityShadingMode = HsVisualQualityShadingMode;

export interface ViewerVisualQualitySettings extends HsVisualQualitySettings {
  profile: ViewerVisualQualityProfile;
  antiAliasing: ViewerVisualQualityAntiAliasing;
  /** Screen-space effects; applied only by the `cad` profile. */
  cad: HsCadEffectsSettings;
}

const BASELINE_VISUAL_QUALITY_SETTINGS: Omit<ViewerVisualQualitySettings, 'profile'> = {
  ...HS_DEFAULT_VISUAL_QUALITY_SETTINGS,
  shadingMode: 'unlit',
  antiAliasing: 'off',
  cad: HS_DEFAULT_CAD_EFFECTS_SETTINGS,
};

// The enhanced looks share the diffuse shading and MSAA of the `enhanced` profile.
const ENHANCED_BASE_OVERRIDES: Partial<Omit<ViewerVisualQualitySettings, 'profile'>> = {
  shadingMode: 'diffuse',
  antiAliasing: 'msaa4',
  specularEnabled: true,
};

const PROFILE_OVERRIDES: Record<ViewerVisualQualityProfile, Partial<Omit<ViewerVisualQualitySettings, 'profile'>>> = {
  baseline: {},
  technical: {
    shadingMode: 'diffuse',
    antiAliasing: 'msaa2',
    ambientIntensity: 0.32,
    directionalIntensity: 0.82,
    fresnelEnabled: true,
    fresnelIntensity: 0.4,
  },
  presentation: {
    shadingMode: 'diffuse',
    antiAliasing: 'msaa4',
    ambientIntensity: 0.42,
    directionalIntensity: 1.12,
    specularEnabled: true,
    specularIntensity: 0.75,
    fresnelEnabled: true,
    fresnelIntensity: 0.45,
  },
  ['high-quality']: {
    shadingMode: 'diffuse',
    antiAliasing: 'msaa8',
    ambientIntensity: 0.38,
    directionalIntensity: 1.02,
    specularEnabled: true,
    specularIntensity: 0.55,
    fresnelEnabled: true,
    fresnelIntensity: 0.45,
    exposure: 0.02,
    contrast: 1.05,
  },
  enhanced: {
    shadingMode: 'diffuse',
    antiAliasing: 'msaa4',
    ambientIntensity: 0.45,
    directionalIntensity: 0.9,
    specularEnabled: true,
    specularIntensity: 0.3,
  },
  ['enhanced-studio']: {
    shadingMode: 'diffuse',
    antiAliasing: 'msaa4',
    ambientIntensity: 0.55,
    directionalIntensity: 0.7,
    specularEnabled: true,
    specularIntensity: 0.2,
  },
  ['enhanced-crisp']: {
    ...ENHANCED_BASE_OVERRIDES,
    ambientIntensity: 0.42,
    directionalIntensity: 0.95,
    specularIntensity: 0.25,
    contrast: 1.06,
  },
  ['enhanced-drawing']: {
    ...ENHANCED_BASE_OVERRIDES,
    ambientIntensity: 0.55,
    directionalIntensity: 0.8,
    specularEnabled: false,
    specularIntensity: 0,
  },
  ['enhanced-balanced']: {
    ...ENHANCED_BASE_OVERRIDES,
    ambientIntensity: 0.45,
    directionalIntensity: 0.9,
    specularIntensity: 0.3,
  },
  ['enhanced-contrast']: {
    ...ENHANCED_BASE_OVERRIDES,
    ambientIntensity: 0.4,
    directionalIntensity: 1,
    specularIntensity: 0.35,
    contrast: 1.1,
  },
  ['studio-soft']: {
    ...ENHANCED_BASE_OVERRIDES,
    ambientIntensity: 0.55,
    directionalIntensity: 0.75,
    specularIntensity: 0.25,
    exposure: 0.08,
  },
  ['studio-gallery']: {
    ...ENHANCED_BASE_OVERRIDES,
    ambientIntensity: 0.62,
    directionalIntensity: 0.7,
    specularIntensity: 0.2,
    exposure: 0.2,
  },
  ['studio-product']: {
    ...ENHANCED_BASE_OVERRIDES,
    ambientIntensity: 0.5,
    directionalIntensity: 0.8,
    specularIntensity: 0.35,
    contrast: 1.08,
  },
  ['studio-clay']: {
    ...ENHANCED_BASE_OVERRIDES,
    ambientIntensity: 0.58,
    directionalIntensity: 0.75,
    specularIntensity: 0.15,
    exposure: 0.1,
  },
};

const PROFILE_CAD_OVERRIDES: Partial<Record<ViewerVisualQualityProfile, Partial<HsCadEffectsSettings>>> = {
  ['enhanced-studio']: {
    ssaoIntensity: 1.4,
    ssaoRadius: 0.05,
    edgeIntensity: 0.95,
    tonemapEnabled: false,
    vignette: true,
  },
  ['enhanced-crisp']: {
    ssaoIntensity: 1,
    ssaoRadius: 0.025,
    edgeIntensity: 0.85,
    edgeWidth: 1,
    tonemapStrength: 0.35,
    sharpen: 0.35,
  },
  ['enhanced-drawing']: {
    ssaoIntensity: 0.8,
    ssaoRadius: 0.02,
    edgeIntensity: 1,
    edgeWidth: 2,
    tonemapEnabled: false,
    saturation: 0.9,
    sharpen: 0.2,
  },
  ['enhanced-balanced']: {
    ssaoIntensity: 1.3,
    ssaoRadius: 0.035,
    edgeIntensity: 0.6,
    tonemapStrength: 0.55,
    keyLight: 0.25,
    saturation: 1.05,
    sharpen: 0.15,
  },
  ['enhanced-contrast']: {
    ssaoIntensity: 1.8,
    ssaoRadius: 0.05,
    edgeIntensity: 0.7,
    tonemapStrength: 0.75,
    saturation: 1.1,
    sharpen: 0.25,
  },
  ['studio-soft']: {
    ssaoIntensity: 1.2,
    ssaoRadius: 0.06,
    edgeIntensity: 0.3,
    tonemapEnabled: true,
    tonemapStrength: 0.8,
    warmth: 0.35,
    keyLight: 0.45,
    saturation: 1.05,
    vignette: true,
  },
  ['studio-gallery']: {
    ssaoIntensity: 0.9,
    ssaoRadius: 0.07,
    edgeIntensity: 0.15,
    tonemapEnabled: true,
    tonemapStrength: 0.6,
    warmth: 0.15,
    keyLight: 0.3,
    saturation: 0.95,
    vignette: true,
  },
  ['studio-product']: {
    ssaoIntensity: 1.6,
    ssaoRadius: 0.05,
    edgeIntensity: 0.45,
    tonemapEnabled: true,
    tonemapStrength: 1,
    warmth: 0.5,
    keyLight: 0.6,
    saturation: 1.1,
    vignette: true,
  },
  ['studio-clay']: {
    ssaoIntensity: 1.5,
    ssaoRadius: 0.06,
    edgeIntensity: 0.25,
    tonemapEnabled: true,
    tonemapStrength: 0.7,
    warmth: 0.2,
    keyLight: 0.5,
    saturation: 0.45,
    vignette: true,
  },
};

export function defaultViewerVisualQualitySettings(): ViewerVisualQualitySettings {
  return viewerVisualQualitySettingsForProfile(DEFAULT_VIEWER_VISUAL_QUALITY_PROFILE);
}

export function viewerVisualQualitySettingsForProfile(
  profile: ViewerVisualQualityProfile
): ViewerVisualQualitySettings {
  return {
    profile,
    ...BASELINE_VISUAL_QUALITY_SETTINGS,
    ...PROFILE_OVERRIDES[profile],
    cad: { ...HS_DEFAULT_CAD_EFFECTS_SETTINGS, ...PROFILE_CAD_OVERRIDES[profile] },
  };
}

export function validateViewerVisualQualitySettings(settings: ViewerVisualQualitySettings): string | null {
  if (
    !VIEWER_VISUAL_QUALITY_PROFILES.some(
      (profile: (typeof VIEWER_VISUAL_QUALITY_PROFILES)[number]) => profile.value === settings.profile
    )
  ) {
    return 'Visual quality profile is not supported.';
  }
  if (settings.shadingMode !== 'unlit' && settings.shadingMode !== 'diffuse') {
    return 'Shading mode: use unlit or diffuse.';
  }
  if (!Number.isFinite(settings.ambientIntensity) || settings.ambientIntensity < 0 || settings.ambientIntensity > 2) {
    return 'Ambient intensity: enter 0 to 2.';
  }
  if (
    !Number.isFinite(settings.directionalIntensity) ||
    settings.directionalIntensity < 0 ||
    settings.directionalIntensity > 3
  ) {
    return 'Directional intensity: enter 0 to 3.';
  }
  if (
    !Number.isFinite(settings.specularIntensity) ||
    settings.specularIntensity < 0 ||
    settings.specularIntensity > 1
  ) {
    return 'Specular intensity: enter 0 to 1.';
  }
  if (!Number.isFinite(settings.fresnelIntensity) || settings.fresnelIntensity < 0 || settings.fresnelIntensity > 1) {
    return 'Fresnel intensity: enter 0 to 1.';
  }
  if (!Number.isFinite(settings.exposure) || settings.exposure < -1 || settings.exposure > 1) {
    return 'Exposure: enter -1 to 1.';
  }
  if (!Number.isFinite(settings.contrast) || settings.contrast < 0.5 || settings.contrast > 1.5) {
    return 'Contrast: enter 0.5 to 1.5.';
  }
  const cadError: string | null = validateCadEffects(settings.cad);
  if (cadError !== null) {
    return cadError;
  }
  return VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS.some(
    (option: (typeof VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS)[number]) => option.value === settings.antiAliasing
  )
    ? null
    : 'Anti-aliasing: use off, msaa2, msaa4, or msaa8.';
}

function validateCadEffects(cad: HsCadEffectsSettings): string | null {
  if (!Number.isFinite(cad.ssaoIntensity) || cad.ssaoIntensity < 0 || cad.ssaoIntensity > 2) {
    return 'Ambient occlusion intensity: enter 0 to 2.';
  }
  if (!Number.isFinite(cad.ssaoRadius) || cad.ssaoRadius < 0.005 || cad.ssaoRadius > 0.08) {
    return 'Ambient occlusion radius: enter 0.005 to 0.08.';
  }
  if (!Number.isFinite(cad.edgeIntensity) || cad.edgeIntensity < 0 || cad.edgeIntensity > 1) {
    return 'Edge intensity: enter 0 to 1.';
  }
  if (cad.edgeWidth !== 1 && cad.edgeWidth !== 2) {
    return 'Edge width: enter 1 or 2.';
  }
  if (!Number.isFinite(cad.tonemapStrength) || cad.tonemapStrength < 0 || cad.tonemapStrength > 1) {
    return 'Tonemap strength: enter 0 to 1.';
  }
  if (!Number.isFinite(cad.saturation) || cad.saturation < 0 || cad.saturation > 2) {
    return 'Saturation: enter 0 to 2.';
  }
  if (!Number.isFinite(cad.warmth) || cad.warmth < -1 || cad.warmth > 1) {
    return 'Warmth: enter -1 to 1.';
  }
  if (!Number.isFinite(cad.keyLight) || cad.keyLight < 0 || cad.keyLight > 1) {
    return 'Key light: enter 0 to 1.';
  }
  if (!Number.isFinite(cad.sharpen) || cad.sharpen < 0 || cad.sharpen > 1) {
    return 'Sharpen: enter 0 to 1.';
  }
  return null;
}

/** True when the screen-space CAD post passes should run for these settings. */
export function viewerCadEffectsActive(settings: ViewerVisualQualitySettings): boolean {
  return VIEWER_CAD_EFFECT_PROFILES.has(settings.profile);
}

export function viewerVisualQualityShaderSignature(settings: ViewerVisualQualitySettings): string {
  return [
    settings.shadingMode,
    settings.ambientIntensity,
    settings.directionalIntensity,
    settings.specularEnabled,
    settings.specularIntensity,
    settings.fresnelEnabled,
    settings.fresnelIntensity,
    settings.exposure,
    settings.contrast,
  ].join('|');
}

export function viewerVisualQualityProfileDefaults(profile: ViewerVisualQualityProfile): ViewerVisualQualitySettings {
  return viewerVisualQualitySettingsForProfile(profile);
}
